import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { homepageSliderService } from '../../../services/homepageSliderService';

const HeroSlider = () => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [slides, setSlides] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Load slides from database
	useEffect(() => {
		const loadSlides = async () => {
			try {
				setLoading(true);
				const { data, error } = await homepageSliderService.getSlides();

				if (error) {
					setError(error);
					return;
				}

				setSlides(data || []);
			} catch (err) {
				setError('Failed to load slides');
				console.error('Error loading slides:', err);
			} finally {
				setLoading(false);
			}
		};

		loadSlides();
	}, []);

	// Debug: Log the current slide image
	console.log('Current slide image:', slides[currentSlide]?.image);

	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % slides?.length);
		}, 8000);

		return () => clearInterval(timer);
	}, [slides?.length]);

	const goToSlide = (index) => {
		setCurrentSlide(index);
	};

	const nextSlide = () => {
		setCurrentSlide((prev) => (prev + 1) % slides?.length);
	};

	const prevSlide = () => {
		setCurrentSlide((prev) => (prev - 1 + slides?.length) % slides?.length);
	};

	// Loading state
	if (loading) {
		return (
			<div className='relative h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4'></div>
					<p className='text-white'>Loading slides...</p>
				</div>
			</div>
		);
	}

	// Error state
	if (error || slides.length === 0) {
		return (
			<div className='relative h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center'>
				<div className='text-center'>
					<Icon name='AlertCircle' size={48} className='mx-auto mb-4 text-red-500' />
					<h3 className='text-white text-xl mb-2'>Error Loading Slides</h3>
					<p className='text-white/80'>{error?.message || 'No slides available'}</p>
				</div>
			</div>
		);
	}

	return (
		<div
			className={`relative h-screen overflow-hidden ${
				currentSlide === 0
					? 'bg-white'
					: 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
			}`}
		>
			<AnimatePresence mode='wait'>
				<motion.div
					key={currentSlide}
					initial={{ opacity: 0, scale: 1.05 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0.98 }}
					transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
					className='absolute inset-0'
				>
					<div
						className='absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1500 ease-out'
						style={{
							backgroundImage: `url(${slides?.[currentSlide]?.image_url})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					/>
					{/* Debug: Test if image loads */}
					<img
						src={slides?.[currentSlide]?.image_url}
						alt='Debug background'
						className='absolute inset-0 w-full h-full object-cover opacity-0'
						onLoad={() =>
							console.log('Image loaded successfully:', slides?.[currentSlide]?.image_url)
						}
						onError={() => console.log('Image failed to load:', slides?.[currentSlide]?.image_url)}
					/>

					{/* Enhanced overlay gradients */}
					{currentSlide === 0 ? (
						<>
							{/* Professional overlay for bookshelve image */}
							<div className='absolute inset-0 bg-gradient-to-r from-[#800020]/20 via-[#800020]/10 to-transparent' />
							<div className='absolute inset-0 bg-gradient-to-l from-black/30 via-black/15 to-transparent' />
							<div className='absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent' />
							<div className='absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/20' />
							{/* Subtle vignette effect */}
							<div className='absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/10' />
							<div className='absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/15' />
						</>
					) : (
						<>
							<div className='absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/70' />
							<div className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent' />
							<div className='absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/40' />
						</>
					)}

					{/* Enhanced Partners image overlay for first slide */}
					{currentSlide === 0 && (
						<motion.div
							initial={{ x: '130%', opacity: 0, scale: 0.6, rotate: 8 }}
							animate={{ x: 0, opacity: 1, scale: 1, rotate: 0 }}
							transition={{ delay: 2.2, duration: 3.0, ease: [0.25, 0.46, 0.45, 0.94] }}
							className='absolute inset-0 flex items-center justify-center lg:justify-end pr-6 lg:pr-16'
						>
							<div className='relative group'>
								{/* Enhanced glow layers */}
								<div className='absolute inset-0 bg-gradient-to-l from-primary/50 via-primary/25 to-transparent rounded-full blur-3xl scale-130 opacity-90' />
								<div className='absolute inset-0 bg-gradient-to-t from-accent/40 via-accent/20 to-transparent rounded-full blur-2xl scale-115 opacity-70' />
								<div className='absolute inset-0 bg-gradient-to-br from-white/15 via-white/5 to-transparent rounded-full blur-xl scale-110 opacity-50' />
								<div className='absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-accent/20 rounded-full blur-lg scale-105 opacity-30' />

								{/* Main image container */}
								<div className='relative z-10 transform group-hover:scale-105 group-hover:rotate-1 transition-all duration-700 ease-out'>
									<div className='relative overflow-hidden rounded-3xl shadow-2xl'>
										{/* Enhanced soft fade overlay */}
										<div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10 pointer-events-none' />
										<div className='absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/40 via-black/10 to-transparent z-10 pointer-events-none' />
										<img
											src='/assets/images/partners.png'
											alt='Legal Partners'
											className='max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl h-auto object-contain drop-shadow-2xl filter brightness-115 contrast-115 saturate-120'
										/>
									</div>
								</div>

								{/* Enhanced floating particles */}
								<motion.div
									animate={{ y: [0, -10, 0], opacity: [0.6, 1, 0.6] }}
									transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
									className='absolute top-1/4 left-1/4 w-3 h-3 bg-primary/70 rounded-full blur-sm'
								/>
								<motion.div
									animate={{ y: [0, -8, 0], opacity: [0.4, 0.8, 0.4] }}
									transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
									className='absolute top-1/3 right-1/3 w-2 h-2 bg-accent/80 rounded-full blur-sm'
								/>
								<motion.div
									animate={{ y: [0, -12, 0], opacity: [0.3, 0.7, 0.3] }}
									transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
									className='absolute bottom-1/3 left-1/3 w-2.5 h-2.5 bg-white/50 rounded-full blur-sm'
								/>
							</div>
						</motion.div>
					)}

					<div className='relative z-10 h-full flex items-center'>
						<div className='w-full max-w-7xl mx-auto px-4 lg:px-8'>
							<div className={`${currentSlide === 0 ? 'max-w-sm lg:max-w-md' : 'max-w-4xl'}`}>
								<motion.div
									initial={{ y: 60, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.6, duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
									className='space-y-8'
								>
									<div className='space-y-6'>
										<motion.h1
											initial={{ y: 40, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											transition={{ delay: 1.2, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
											className='font-playfair font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight uppercase'
										>
											<span className='block bg-gradient-to-r from-white via-white to-white/95 bg-clip-text text-transparent drop-shadow-lg'>
												{slides?.[currentSlide]?.title?.split(' ').slice(0, 3).join(' ')}
											</span>
											<span className='block bg-gradient-to-r from-white via-white to-white/95 bg-clip-text text-transparent mt-3 drop-shadow-lg'>
												{slides?.[currentSlide]?.title?.split(' ').slice(3).join(' ')}
											</span>
										</motion.h1>
									</div>

									<motion.div
										initial={{ y: 30, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 1.4, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
										className='space-y-4'
									>
										<p className='font-source text-base md:text-lg text-white/95 font-semibold italic max-w-lg leading-relaxed drop-shadow-md'>
											{slides?.[currentSlide]?.subtitle}
										</p>
									</motion.div>

									<motion.div
										initial={{ y: 40, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 1.6, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
										className='flex flex-col sm:flex-row gap-4 pt-4'
									>
										<Button
											variant='default'
											className='bg-primary hover:bg-primary/90 text-white font-source font-bold px-8 py-3 text-sm shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105'
											iconName='ArrowRight'
											iconPosition='right'
											iconSize={18}
										>
											{slides?.[currentSlide]?.cta}
										</Button>

										<Button
											variant='outline'
											className='border-2 border-white/90 text-white hover:bg-white hover:text-primary font-source font-bold px-8 py-3 text-sm transition-all duration-300 transform hover:scale-105'
											iconName='Phone'
											iconPosition='left'
											iconSize={18}
										>
											Call Now: (+234) 909-552-9557
										</Button>
									</motion.div>
								</motion.div>
							</div>
						</div>
					</div>
				</motion.div>
			</AnimatePresence>

			{/* Enhanced Navigation Controls */}
			<div className='absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20'>
				<div className='flex items-center space-x-6 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 border border-white/20'>
					<button
						onClick={prevSlide}
						className='w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all duration-300 shadow-lg'
						aria-label='Previous slide'
					>
						<Icon name='ChevronLeft' size={28} />
					</button>

					<div className='flex space-x-1.5'>
						{slides?.map((_, index) => (
							<button
								key={index}
								onClick={() => goToSlide(index)}
								className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
									index === currentSlide
										? 'bg-accent scale-125 shadow-md shadow-accent/40'
										: 'bg-white/40 hover:bg-white/60 hover:scale-110'
								}`}
								aria-label={`Go to slide ${index + 1}`}
							/>
						))}
					</div>

					<button
						onClick={nextSlide}
						className='w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all duration-300 shadow-lg'
						aria-label='Next slide'
					>
						<Icon name='ChevronRight' size={28} />
					</button>
				</div>
			</div>

			{/* Enhanced Scroll Indicator */}
			<div className='absolute bottom-12 right-12 z-20'>
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 2, duration: 0.8 }}
					className='flex flex-col items-center space-y-3 text-white/80'
				>
					<span className='font-source text-sm tracking-wider uppercase'>Scroll</span>
					<motion.div
						animate={{ y: [0, 12, 0] }}
						transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
						className='w-6 h-10 border-2 border-white/60 rounded-full flex justify-center'
					>
						<motion.div
							animate={{ y: [0, 12, 0] }}
							transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
							className='w-1 h-3 bg-white/80 rounded-full mt-2'
						/>
					</motion.div>
				</motion.div>
			</div>
		</div>
	);
};

export default HeroSlider;
